import { AccountSearchInput } from './AccountSearchInput';
import { Input, FormField } from '../../../components/ui/FormField';
import { CustomerPicker } from '../../customers/components/CustomerPicker';
import { OrgUnitPicker } from '../../org/components/OrgUnitPicker';
import { UserPicker } from '../../../components/pickers/UserPicker';
import type { GeneralTransactionLineInput } from '../network/general-transactions.api';

interface GeneralTransactionLineFieldsProps {
  line: GeneralTransactionLineInput;
  onChange: (line: GeneralTransactionLineInput) => void;
  disabled?: boolean;
  showAssociation?: boolean;
}

const ASSOCIATION_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'BRANCH', label: 'Branch / Org Unit' },
  { value: 'STAFF', label: 'Staff' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'PARTY', label: 'Party' },
] as const;

export function GeneralTransactionLineFields({ line, onChange, disabled, showAssociation = true }: GeneralTransactionLineFieldsProps) {
  const renderAssociationPicker = () => {
    if (!line.associationType) return null;

    const handleSelect = (id: string) => {
      onChange({ ...line, associationId: id || null });
    };

    if (line.associationType === 'MEMBER' || line.associationType === 'PARTY') {
      return (
        <CustomerPicker
          value={line.associationId ?? ''}
          onChange={handleSelect}
          placeholder="Search member/customer..."
          disabled={disabled}
        />
      );
    }

    if (line.associationType === 'BRANCH') {
      return (
        <OrgUnitPicker
          value={line.associationId ?? ''}
          onChange={handleSelect}
          placeholder="Select branch..."
          disabled={disabled}
        />
      );
    }

    if (line.associationType === 'STAFF') {
      return (
        <UserPicker
          value={line.associationId ?? ''}
          onChange={handleSelect}
          placeholder="Search staff..."
          disabled={disabled}
        />
      );
    }

    return (
      <Input
        placeholder="Enter reference ID..."
        className="w-full font-mono text-sm"
        value={line.associationId ?? ''}
        onChange={(e) => onChange({ ...line, associationId: e.target.value || null })}
        disabled={disabled}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField label="Account" required>
        <AccountSearchInput
          value={line.accountId}
          onChange={(accountId) => onChange({ ...line, accountId })}
          disabled={disabled}
        />
      </FormField>

      <FormField label="Narration">
        <Input
          placeholder="Line narration..."
          className="w-full"
          value={line.narration ?? ''}
          onChange={(e) => onChange({ ...line, narration: e.target.value })}
          disabled={disabled}
        />
      </FormField>

      {showAssociation && (
        <FormField label="Associated with">
          <select
            className="w-full border border-surface-3 rounded px-2 py-2 text-sm bg-surface-0"
            value={line.associationType ?? ''}
            onChange={(e) => {
              const associationType = (e.target.value || null) as GeneralTransactionLineInput['associationType'];
              onChange({ ...line, associationType, associationId: null });
            }}
            disabled={disabled}
          >
            {ASSOCIATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </FormField>
      )}

      {line.associationType && (
        <FormField label="Associated Entity">
          {renderAssociationPicker()}
        </FormField>
      )}
    </div>
  );
}


